/**
 * Payment card helpers shared by the Add Card / Update Card form.
 *
 * Pure functions only, so they can be unit tested without rendering the form.
 * The form keeps the card number as digits only and the expiry as "MM/YY"; spaces are display only.
 */

const ARABIC_INDIC_ZERO = 0x0660; // ٠ to ٩
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0; // ۰ to ۹ (Persian / Urdu keyboards)

export const toWesternDigits = (value) =>
  String(value == null ? "" : value).replace(/[٠-٩۰-۹]/g, (digit) => {
    const code = digit.charCodeAt(0);
    return String(code - (code >= EXTENDED_ARABIC_INDIC_ZERO ? EXTENDED_ARABIC_INDIC_ZERO : ARABIC_INDIC_ZERO));
  });

export const digitsOnly = (value) => toWesternDigits(value).replace(/\D/g, "");

// Mada (Saudi debit) BINs. They sit inside the Visa / Mastercard ranges, so Mada must be checked first.
const MADA_BINS = [
  "588845", "440647", "440795", "446404", "457865", "968208", "457997", "474491", "636120", "417633",
  "468540", "468541", "468542", "468543", "968201", "446393", "409201", "458456", "484783", "462220",
  "455708", "410621", "455036", "486094", "486095", "486096", "504300", "440533", "489318", "489319",
  "445564", "968211", "410685", "406996", "432328", "428671", "428672", "428673", "968206", "446672",
  "543357", "434107", "407197", "407395", "412565", "431361", "604906", "521076", "588850", "968202",
  "529415", "535825", "543085", "524130", "554180", "549760", "968209", "524514", "529741", "537767",
  "535989", "536023", "513213", "520058", "558563", "588848", "588982", "589005", "531095", "530906",
  "532013", "968204", "422817", "422818", "422819", "428331", "483010", "483011", "483012", "589206",
  "968207", "419593", "439954", "407520", "530060", "531196", "420132", "242030", "968203", "552363",
  "968205", "552438", "585265", "588983", "588984", "588851", "968210",
];

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

/**
 * Card brands, checked in order. To add a brand, add a row:
 *  - matches: tells whether the (partial) digits belong to the brand
 *  - lengths: the valid card number lengths
 *  - gaps:    the size of each display group; digits past the last group are grouped in 4s
 */
export const CARD_BRANDS = [
  { type: "mada", matches: (digits) => MADA_BINS.includes(digits.slice(0, 6)), lengths: [16], gaps: [4, 4, 4, 4] },
  { type: "amex", matches: (digits) => /^3[47]/.test(digits), lengths: [15], gaps: [4, 6, 5] },
  { type: "diners", matches: (digits) => /^3(0[0-5]|09|[689])/.test(digits), lengths: [14], gaps: [4, 6, 4] },
  { type: "jcb", matches: (digits) => /^35(2[89]|[3-8])/.test(digits), lengths: range(16, 19), gaps: [4, 4, 4, 4] },
  { type: "visa", matches: (digits) => /^4/.test(digits), lengths: [13, 16, 19], gaps: [4, 4, 4, 4] },
  {
    type: "mastercard",
    matches: (digits) => /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/.test(digits),
    lengths: [16],
    gaps: [4, 4, 4, 4],
  },
  { type: "discover", matches: (digits) => /^(6011|64[4-9]|65)/.test(digits), lengths: range(16, 19), gaps: [4, 4, 4, 4] },
  { type: "unionpay", matches: (digits) => /^(62|81)/.test(digits), lengths: range(16, 19), gaps: [4, 4, 4, 4] },
];

export const UNKNOWN_CARD_BRAND = { type: "unknown", lengths: range(12, 19), gaps: [4, 4, 4, 4] };

export const detectCardBrand = (value) => {
  const digits = digitsOnly(value);
  return (digits && CARD_BRANDS.find((brand) => brand.matches(digits))) || UNKNOWN_CARD_BRAND;
};

export const getCardMaxLength = (value) => Math.max(...detectCardBrand(value).lengths);

/** Digits only, cut to the longest length allowed for the detected brand */
export const normalizeCardNumber = (value) => {
  const digits = digitsOnly(value);
  return digits.slice(0, getCardMaxLength(digits));
};

export const isCardNumberLengthValid = (value) => {
  const digits = digitsOnly(value);
  return detectCardBrand(digits).lengths.includes(digits.length);
};

/** "4548870450051733" -> "4548 8704 5005 1733", grouped by the detected brand */
export const formatCardNumber = (value) => {
  const digits = normalizeCardNumber(value);
  const { gaps } = detectCardBrand(digits);
  const groups = [];
  let start = 0;
  let index = 0;
  while (start < digits.length) {
    const size = gaps[index] || 4;
    groups.push(digits.slice(start, start + size));
    start += size;
    index += 1;
  }
  return groups.join(" ");
};

/** Position in the formatted value that sits right after the given number of digits */
export const caretAfterDigits = (formatted, digitCount) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitCount) return i + 1;
    }
  }
  return formatted.length;
};

/**
 * Accepts "MM/YY", "MM/YYYY", "MMYY", "MMYYYY", "MM-YY", "M/YY" (also with Arabic-Indic digits)
 * and returns "MM/YY". Returns null when the value is not a complete expiry. The month range is not
 * checked here, see parseExpiry.
 */
export const normalizeExpiry = (value) => {
  const text = toWesternDigits(value).trim();
  const match = /^(\d{2})\s*[/\-. ]?\s*(\d{2}|\d{4})$/.exec(text) || /^(\d)\s*[/\-. ]\s*(\d{2}|\d{4})$/.exec(text);
  if (!match) return null;
  const [, month, year] = match;
  if (year.length === 4 && !year.startsWith("20")) return null;
  return `${month.padStart(2, "0")}/${year.slice(-2)}`;
};

/** { month: 1-12, year: 20YY } or null */
export const parseExpiry = (value) => {
  const normalized = normalizeExpiry(value);
  if (!normalized) return null;
  const [month, year] = normalized.split("/").map(Number);
  if (month < 1 || month > 12) return null;
  return { month, year: 2000 + year };
};

/** A card is valid through the last day of its expiry month */
export const isExpiryValid = (value, now = new Date()) => {
  const expiry = parseExpiry(value);
  if (!expiry) return false;
  return expiry.year * 12 + (expiry.month - 1) >= now.getFullYear() * 12 + now.getMonth();
};

/**
 * Formats the expiry while the user types: adds "/" after the month and lets backspace remove it.
 * A pasted full value ("05/2031", "0531", "05-31") is normalized to "05/31".
 */
export const formatExpiryInput = (value, { deleting = false } = {}) => {
  const pasted = !deleting && normalizeExpiry(value);
  if (pasted) return pasted;

  let digits = digitsOnly(value).slice(0, 4);
  if (!deleting && digits.length === 1) {
    // A first digit of 2-9 can only be a single digit month, and "1/" typed by hand means January
    const typedSeparator = /^\d\s*[/\-.]/.test(toWesternDigits(value).trim());
    if (/[2-9]/.test(digits) || typedSeparator) digits = `0${digits}`;
  }

  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  if (digits.length === 2 && !deleting) return `${digits}/`;
  return digits;
};
